import {
  AnalyticsEvent,
  Button,
  CVEditingSection,
  CVSensitiveInfo,
  DEFAULT_TEMPLATE_ID,
  FullCV,
  FullCVWithTemplate,
  TemplateId,
} from '@cibli/shared';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainLayout from '@/features/common/components/MainLayout';
import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { useTrackEvent } from '@/lib/analytics/use-track-event';
import { genderizeCv, type GenderizeCvPayload } from '@/services/interview';

import { CvThemeShadowRenderer } from '../../cv-theme-picker/CvThemeShadowRenderer';
import { fetchCvThemePreviewHtml } from '../../cv-theme-picker/use-cv-themes';
import GenderChangeModal from '../components/animations/GenderChangeModal';
import GenderPulse from '../components/animations/GenderPulse';
import PageDelimiter from '../components/PageDelimiter';
import {
  PageDelimitationScaleProvider,
  PageDelimiterContentHeightProvider,
} from '../contexts/page-delimitation-scale.providers';
import { VocalCVEditProvider } from '../contexts/vocal-cv-edit.context';
import { useCVPreviewScale } from '../hooks/useCVPreviewScale';
import { getHeadingColor } from '../templates/helpers';
import { getTemplate } from '../templates/registry';
import TemplateHost from '../templates/TemplateHost';

// Sentinel key for neutre (null) gender in the Map — avoids ambiguous null keys
const NEUTRE = '__neutre__' as const;
const toKey = (g: string | null): string => (g === null ? NEUTRE : g);

interface Props {
  cv: FullCV;
  isGeneratingCV: boolean;
  onEdit: (data: FullCV) => void;
  onGenerateCV: () => void;
  formConfirmationText?: string;
  headerActions?: React.ReactNode;
  backButton?: React.ReactNode;
  statusBar?: React.ReactNode;
}

export default function CVDisplayScreen(props: Props) {
  return (
    <VocalCVEditProvider>
      <CVDisplayScreenInner {...props} />
    </VocalCVEditProvider>
  );
}

function CVDisplayScreenInner({
  cv,
  onEdit,
  onGenerateCV,
  isGeneratingCV,
  formConfirmationText,
  headerActions,
  backButton,
  statusBar,
}: Props) {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const { cvTemplateId, cvTemplateParams } = useCVTemplateContext();
  const template = useMemo(() => getTemplate(cvTemplateId || DEFAULT_TEMPLATE_ID), [cvTemplateId]);
  const initialParams = useMemo(() => {
    const parsed = template.schema.safeParse(cvTemplateParams ?? template.defaults);
    return parsed.success ? parsed.data : template.defaults;
  }, [cvTemplateParams, template]);
  const [params, setParams] = useState<Record<string, unknown>>(initialParams);
  const [isEditingSection, setIsEditingSection] = useState(false);
  const [isGenderizing, setIsGenderizing] = useState(false);
  const [pendingGender, setPendingGender] = useState<string | null>(null);
  const trackEvent = useTrackEvent();
  const cvContainerRef = useRef<HTMLDivElement>(null);
  const originalCvRef = useRef<FullCV>(cv);
  const originalCvSavedRef = useRef(false);
  const genderCacheRef = useRef<Map<string, FullCV>>(new Map());

  // JSON Resume themes (feature-flagged)
  const [selectedCvThemeId] = useState<string | null>(null);
  const [cvThemeEditingSection, setCvThemeEditingSection] = useState<CVEditingSection | null>(null);
  const [cvThemeHtml, setCvThemeHtml] = useState<string | null>(null);
  const [isCvThemeLoading, setIsCvThemeLoading] = useState(false);

  // Fetch HTML preview when a JSON Resume theme is selected (re-renders on accent/spacing change)
  const accentColor = params.accentColor as string | undefined;
  const spacing = (params.spacing as string) ?? 'normal';
  const headingColor = accentColor ? getHeadingColor(accentColor) : undefined;
  const [editGeneration, setEditGeneration] = useState(0);
  useEffect(() => {
    if (!selectedCvThemeId || !cv) {
      setCvThemeHtml(null);
      return;
    }
    let cancelled = false;
    setIsCvThemeLoading(true);
    const themeParams: Record<string, unknown> = {};
    if (accentColor) themeParams.accentColor = accentColor;
    if (headingColor) themeParams.headingColor = headingColor;
    if (spacing) themeParams.spacing = spacing;
    fetchCvThemePreviewHtml(
      cv,
      selectedCvThemeId,
      Object.keys(themeParams).length > 0 ? themeParams : undefined,
    )
      .then(html => {
        if (!cancelled) setCvThemeHtml(html);
      })
      .catch(() => {
        if (!cancelled) setCvThemeHtml(null);
      })
      .finally(() => {
        if (!cancelled) setIsCvThemeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCvThemeId, cv, accentColor, headingColor, spacing, editGeneration]);

  // Re-fetch when exiting edit mode to ensure preview reflects any edits
  useEffect(() => {
    if (!isEditingSection && selectedCvThemeId) {
      setEditGeneration(g => g + 1);
    }
  }, [isEditingSection, selectedCvThemeId]);

  const scaleState = useCVPreviewScale({
    contentKey: [
      cv.competences?.length ?? 0,
      cv.experiences?.length ?? 0,
      cv.education?.length ?? 0,
      cv.personalExperiences?.length ?? 0,
      cv.summary?.length ?? 0,
    ].join('-'),
  });
  const {
    isNarrowViewport,
    scale,
    lastPageSpacerHeight,
    contentHeightWithoutSpacer,
    pdfViewportRef,
    cvContentRef,
    wrapperStyle,
    viewportClassName,
    viewportStyle,
  } = scaleState;

  useMemo(() => {
    const parsed = template.schema.safeParse(cvTemplateParams ?? template.defaults);
    setParams(parsed.success ? parsed.data : template.defaults);
  }, [template, cvTemplateParams]);

  const handleGenerateCV = useCallback(() => {
    onGenerateCV();
    trackEvent({ event: AnalyticsEvent.DOWNLOADED_CV });
  }, [onGenerateCV, trackEvent]);

  // Seed gender cache on mount: save the original (masculine/neutre) CV
  // so neutre restore always returns the correct pre-genderized form.
  // Only seed if CV is NOT already feminized — if it is, the feminine CV is
  // the "original" and we must not cache it as masculine/neutre.
  useEffect(() => {
    if (!cv.gender && !genderCacheRef.current.has(NEUTRE)) {
      genderCacheRef.current.set(NEUTRE, cv);
      genderCacheRef.current.set('homme', cv);
    }
  }, [cv]);

  const handleGenderChange = useCallback(
    (gender: string | null) => {
      // 1. Same gender → no-op (preserves edits)
      if (cv.gender === gender) return;

      const cacheKey = toKey(gender);

      // 2. Fast path: check cache
      const cached = genderCacheRef.current.get(cacheKey);
      if (cached) {
        onEdit({ ...cached, gender: gender ?? null });
        setPendingGender(null);
        return;
      }

      // 3. Save original + seed cache on first change
      if (!originalCvSavedRef.current) {
        originalCvSavedRef.current = true;
        const origGender = cv.gender ?? null;
        genderCacheRef.current.set(toKey(origGender), cv);
        // neutre ≈ homme in French
        if (origGender === null) {
          genderCacheRef.current.set('homme', cv);
        }
      }

      // 4. Neutre: restore original (instant, no GPT)
      if (gender === null) {
        const original = originalCvRef.current;
        // Only seed cache if not already set (mount effect or step 3 may have set it)
        if (!genderCacheRef.current.has(NEUTRE)) {
          genderCacheRef.current.set(NEUTRE, original);
        }
        onEdit({ ...original, gender: null });
        setPendingGender(null);
        return;
      }

      // 5. Check if target gender matches original (instant restore)
      const original = originalCvRef.current;
      if (original) {
        const origGender = original.gender ?? null;
        const isEquivalent = origGender === gender || (origGender === null && gender === 'homme');
        if (isEquivalent) {
          genderCacheRef.current.set(gender, original);
          onEdit({ ...original, gender });
          setPendingGender(null);
          return;
        }
      }

      // 6. No GPT needed (homme ↔ neutre when not from original)
      const needsGpt = gender === 'femme' || cv.gender === 'femme';
      if (!needsGpt) {
        const result = { ...cv, gender };
        genderCacheRef.current.set(gender, result);
        onEdit(result);
        setPendingGender(null);
        return;
      }

      // 7. GPT call — always send original CV, not current
      setPendingGender(gender);
      setIsGenderizing(true);
      const timeoutId = setTimeout(() => {
        setIsGenderizing(false);
        setPendingGender(null);
      }, 30_000);

      void (async () => {
        try {
          const originalForPayload = originalCvRef.current ?? cv;
          const payload: GenderizeCvPayload = {
            cv: originalForPayload,
            gender: gender as 'homme' | 'femme',
            userId: user?.id,
            interviewId: (originalForPayload as FullCVWithTemplate).interviewId,
          };
          const { cv: genderizedCv } = await genderizeCv(payload);
          genderCacheRef.current.set(gender, genderizedCv);
          onEdit(genderizedCv);
        } catch (error) {
          console.error('Error genderizing CV:', error);
        } finally {
          clearTimeout(timeoutId);
          setIsGenderizing(false);
          setPendingGender(null);
        }
      })();
    },
    [cv, user, onEdit],
  );

  const sensitiveInfo: CVSensitiveInfo | null = useMemo(() => {
    return {
      firstName: cv.firstName ?? user?.firstName,
      lastName: cv.lastName ?? user?.lastName,
      phone: cv.phone ?? user?.phoneNumber,
      email: cv.email === null ? null : (cv.email ?? user?.email ?? null),
    };
  }, [user, cv]);

  if (!sensitiveInfo) return null;

  return (
    <MainLayout
      align="top-center"
      padding="pt-4 pb-32 sm:pt-6"
    >
      <h1 className="text-xl font-bold text-[#254441] text-center sm:text-2xl">
        Modifiez votre CV
      </h1>
      {statusBar}
      {/* Back button + header actions — above the flex so lists align with CV header */}
      {(backButton || (headerActions && !isEditingSection)) && (
        <div className="flex items-center justify-between gap-2 mb-2 w-full max-w-[210mm] mx-auto">
          {backButton && <div>{backButton}</div>}
          {headerActions && !isEditingSection && (
            <div className="flex items-center gap-2">{headerActions}</div>
          )}
        </div>
      )}
      <div className="flex gap-6 w-full justify-center items-start">
        <div className="relative max-w-[210mm] w-full flex flex-col min-w-0">
          <div
            ref={cvContainerRef}
            className={isEditingSection ? 'relative' : 'relative min-h-[1123px]'}
          >
            {/* JSON Resume theme: show either iframe preview or TemplateHost for editing */}
            {selectedCvThemeId ? (
              isEditingSection ? (
                /* Editing mode — TemplateHost renders for section interaction */
                <>
                  <TemplateHost
                    cv={cv}
                    mode="edit"
                    templateId={(cvTemplateId || DEFAULT_TEMPLATE_ID) as TemplateId}
                    initialParams={params}
                    initialEditingSection={cvThemeEditingSection}
                    onEditCV={onEdit}
                    onEditingSectionChange={value => {
                      setIsEditingSection(value);
                    }}
                  />
                  {!isEditingSection && (
                    <PageDelimiter
                      key={`page-delimiter-${cvTemplateId}`}
                      containerRef={cvContainerRef}
                    />
                  )}
                </>
              ) : (
                /* Preview mode — Shadow DOM with section edit overlay */
                <CvThemeShadowRenderer
                  html={cvThemeHtml}
                  isLoading={isCvThemeLoading}
                  onSectionClick={section => {
                    setCvThemeEditingSection(section as CVEditingSection);
                    setIsEditingSection(true);
                  }}
                />
              )
            ) : isNarrowViewport && !isEditingSection ? (
              <div className="relative overflow-hidden" style={wrapperStyle}>
                <div ref={pdfViewportRef} className={viewportClassName} style={viewportStyle}>
                  <PageDelimitationScaleProvider value={scale}>
                    <PageDelimiterContentHeightProvider value={contentHeightWithoutSpacer}>
                      <div ref={cvContentRef} className="relative">
                        <TemplateHost
                          cv={cv}
                          mode="edit"
                          templateId={(cvTemplateId || DEFAULT_TEMPLATE_ID) as TemplateId}
                          initialParams={params}
                          onEditCV={onEdit}
                          onEditingSectionChange={value => {
                            setIsEditingSection(value);
                          }}
                        />
                        {!cvTemplateId?.startsWith('json-') && (
                          <PageDelimiter
                            key={`page-delimiter-${cvTemplateId}`}
                            containerRef={pdfViewportRef}
                          />
                        )}
                      </div>
                    </PageDelimiterContentHeightProvider>
                    {lastPageSpacerHeight > 0 && (
                      <div
                        className="flex-shrink-0"
                        style={{ height: lastPageSpacerHeight }}
                        aria-hidden
                      />
                    )}
                  </PageDelimitationScaleProvider>
                </div>
              </div>
            ) : (
              <>
                <TemplateHost
                  cv={cv}
                  mode="edit"
                  templateId={(cvTemplateId || DEFAULT_TEMPLATE_ID) as TemplateId}
                  initialParams={params}
                  onEditCV={onEdit}
                  onEditingSectionChange={value => {
                    setIsEditingSection(value);
                  }}
                />
                {!isEditingSection && !cvTemplateId?.startsWith('json-') && (
                  <PageDelimiter
                    key={`page-delimiter-${cvTemplateId}`}
                    containerRef={cvContainerRef}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {!isEditingSection && (
        <div className="fixed bottom-[72px] left-0 right-0 z-50 flex flex-col items-center gap-2 pb-4 pointer-events-none">
          <div className="flex flex-col items-center gap-1.5 text-sm pointer-events-auto rounded-2xl bg-white/95 backdrop-blur-sm px-6 py-3 shadow-lg border border-gray-200">
            <span className="text-xs text-gray-600">{t('CV.GenderSelect.label')}</span>
            <div className="flex gap-1">
              {(
                [
                  { value: null, label: t('CV.GenderSelect.neutral') },
                  { value: 'homme', label: t('CV.GenderSelect.male') },
                  { value: 'femme', label: t('CV.GenderSelect.female') },
                ] as const
              ).map(option => {
                const isSelected =
                  cv.gender === option.value ||
                  (pendingGender !== null && pendingGender === option.value);
                const isPending = pendingGender !== null && pendingGender === option.value;

                return (
                  <Button
                    key={option.label}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    className={
                      isSelected
                        ? 'bg-[#21c294] !text-white !shadow-md hover:bg-[#1ab386]'
                        : '!border-gray-300 !text-gray-700 hover:!bg-gray-100'
                    }
                    onClick={() => handleGenderChange(option.value)}
                    disabled={isGenderizing && !isSelected}
                  >
                    {isPending ? <GenderPulse /> : null}
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>
          <Button
            disabled={isGeneratingCV}
            size="lg"
            onClick={handleGenerateCV}
            className="pointer-events-auto shadow-xl"
          >
            {formConfirmationText ?? t('Forms.validate')}
            {isGeneratingCV && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
          </Button>
        </div>
      )}
      <GenderChangeModal show={isGenderizing} gender={pendingGender} />
    </MainLayout>
  );
}
